import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { PlayCircle, History, Clock, Terminal, Briefcase } from 'lucide-react';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { useMemo, useEffect } from 'react';
import { TerminalLoader } from '@/components/ui/TerminalLoader';

export function DashboardPage() {
 const { user } = useAuth();
 const {
  analytics,
  fetchAnalytics,
  isLoading,
  stats: backendStats,
 } = useAnalyticsStore();

 useEffect(() => {
  fetchAnalytics();
 }, [fetchAnalytics]);

 const statsData = useMemo(() => {
  if (backendStats) return backendStats;

  const uniqueSessions = new Set(analytics.map((a) => a.sessionId)).size;
  const completedSessions = new Set(
   analytics.filter((a) => a.completedAt).map((a) => a.sessionId)
  ).size;
  const completionRate =
   uniqueSessions > 0
    ? Math.round((completedSessions / uniqueSessions) * 100)
    : 0;

  // Fallback / Client-side calculation for hours if needed (approx)
  const totalHoursHelper =
   analytics.reduce((acc, curr) => acc + (curr.timeSpentSeconds || 0), 0) /
   3600;

  return {
   totalSessions: 0, // Force 0 as per user request if no backend stats
   totalProfiles: 0, // Fallback
   totalHoursSpent: Math.round(totalHoursHelper * 10) / 10,
   avgResponseTime: '0s',
   completionRate: `${completionRate}%`,
  };
 }, [analytics, backendStats]);

 const stats = [
  {
   label: 'TOTAL SESSIONS',
   value: statsData.totalSessions.toString(),
   icon: History,
   trend: 'System Active',
  },
  {
   label: 'JOB PROFILES',
   value: statsData.totalProfiles.toString(),
   icon: Briefcase,
   trend: 'Active Targets',
  },
  {
   label: 'HOURS SPENT',
   value: statsData.totalHoursSpent.toString(),
   icon: Clock,
   trend: 'Training Time',
  },
  {
   label: 'AVG. RESPONSE',
   value: statsData.avgResponseTime || 'N/A',
   icon: Terminal,
   trend: 'Latency',
  },
 ];

 const recentActivity = analytics.slice(0, 5).map((item) => ({
  action: item.stepName || 'System Action',
  target: `Session ${item.sessionId.substring(0, 8)}`,
  time: new Date(item.startedAt).toLocaleDateString(),
  status: item.completedAt ? 'COMPLETED' : 'IN_PROGRESS',
 }));

 if (isLoading) {
  return <TerminalLoader text='ANALYZING_SYSTEM_METRICS...' />;
 }

 return (
  <AppLayout>
   <div className='max-w-7xl mx-auto'>
    {/* Header */}
    <div className='mb-12 border-b border-border pb-8'>
     <div className='flex items-center gap-2 mb-2'>
      <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse' />
      <span className='font-mono text-xs text-green-500'>SYSTEM ONLINE</span>
     </div>
     <h1 className='text-3xl font-bold font-mono tracking-tight mb-2'>
      DASHBOARD
     </h1>
     <p className='text-muted-foreground font-mono text-sm'>
      Welcome back,{' '}
      <span className='text-foreground font-bold'>
       {user?.fullName || 'User'}
      </span>
      . System is ready for simulation.
     </p>
    </div>

    {/* Metrics Grid */}
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12'>
     {stats.map((stat, i) => (
      <div
       key={i}
       className='border border-border p-6 bg-background hover:border-primary/50 transition-colors'
      >
       <div className='flex justify-between items-start mb-4'>
        <div className='p-2 bg-muted/20 rounded-none'>
         <stat.icon className='w-5 h-5 text-primary' />
        </div>
        {i === 0 && <div className='w-1.5 h-1.5 bg-primary rounded-full' />}
       </div>
       <div className='space-y-1'>
        <h3 className='text-3xl font-bold tracking-tighter'>{stat.value}</h3>
        <p className='font-mono text-xs text-muted-foreground'>{stat.label}</p>
       </div>
       <div className='mt-4 pt-4 border-t border-border/50'>
        <span className='text-xs font-mono text-green-500'>{stat.trend}</span>
       </div>
      </div>
     ))}
    </div>

    <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
     {/* Main Action Area */}
     <div className='lg:col-span-2 space-y-8'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
       {/* Quick Start */}
       <div className='group border border-border bg-background hover:border-primary transition-all duration-300 relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-1 h-full bg-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300' />
        <div className='p-6 h-full flex flex-col'>
         <div className='mb-4 w-12 h-12 bg-muted/20 flex items-center justify-center'>
          <PlayCircle className='w-6 h-6 text-primary' />
         </div>
         <h3 className='text-xl font-bold mb-2'>INITIALIZE SESSION</h3>
         <p className='text-sm text-muted-foreground mb-8 flex-1'>
          Launch a new high-fidelity interview simulation using active profiles.
         </p>
         <Link to='/interview' className='block'>
          <Button className='w-full rounded-none font-mono text-xs bg-primary text-primary-foreground hover:bg-primary/90'>
           <PlayCircle className='w-3 h-3 mr-2' /> START SIMULATION
          </Button>
         </Link>
        </div>
       </div>

       {/* Subscription Status */}
       <div className='group border border-border bg-background hover:border-primary transition-all duration-300 relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-1 h-full bg-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300' />
        <div className='p-6 h-full flex flex-col'>
         <div className='mb-4 w-12 h-12 bg-muted/20 flex items-center justify-center'>
          <Terminal className='w-6 h-6 text-muted-foreground' />
         </div>
         <h3 className='text-xl font-bold mb-2'>SUBSCRIPTION</h3>
         <p className='text-sm text-muted-foreground mb-8 flex-1'>
          Current Plan:{' '}
          <span className='text-primary font-mono'>
           {user?.subscriptionTier || 'FREE'}
          </span>
          . View limits and usage details.
         </p>
         <Link to='/billing' className='block'>
          <Button
           className='w-full rounded-none font-mono text-xs'
           variant='outline'
          >
           MANAGE PLAN
          </Button>
         </Link>
        </div>
       </div>
      </div>
     </div>

     {/* Recent Activity */}
     <div className='border border-border bg-background h-fit'>
      <div className='px-6 py-4 border-b border-border flex items-center justify-between'>
       <h3 className='font-mono text-sm font-bold'>RECENT ACTIVITY</h3>
       <div className='flex gap-2'>
        <div className='w-1.5 h-1.5 rounded-full bg-muted-foreground/30' />
        <div className='w-1.5 h-1.5 rounded-full bg-muted-foreground/30' />
       </div>
      </div>
      <div className='divide-y divide-border'>
       {recentActivity.map((activity, i) => (
        <div key={i} className='p-4 hover:bg-muted/5 transition-colors'>
         <div className='flex justify-between items-start mb-1'>
          <span className='font-bold text-sm'>{activity.action}</span>
          <span className='font-mono text-[10px] text-muted-foreground'>
           {activity.time}
          </span>
         </div>
         <p className='text-xs text-muted-foreground mb-2'>{activity.target}</p>
         <span className='inline-block px-1.5 py-0.5 border border-border text-[10px] font-mono text-muted-foreground'>
          {activity.status}
         </span>
        </div>
       ))}
       <div className='p-4 text-center'>
        <Button
         variant='ghost'
         className='w-full font-mono text-xs hover:bg-transparent hover:text-primary'
        >
         VIEW ALL ACTIVITY →
        </Button>
       </div>
      </div>
     </div>
    </div>
   </div>
  </AppLayout>
 );
}
